import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { KeyRound, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api';
import { useAuth } from './AuthProvider';
import { AuthLayout } from './AuthLayout';

export function LoginPage() {
  const { login, status, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [needsSecondFactor, setNeedsSecondFactor] = useState(false);
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === 'authenticated' && user) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? user.preferences.defaultView ?? '/my-day'} replace />;
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({
        email: email.trim(),
        password,
        ...(needsSecondFactor && !useRecovery && totp ? { totp } : {}),
        ...(needsSecondFactor && useRecovery && recoveryCode ? { recoveryCode } : {}),
      });
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'TWO_FACTOR_REQUIRED') {
          setNeedsSecondFactor(true);
          setError(null);
        } else {
          setError(err.message);
        }
      } else {
        setError('Could not reach the server. Check your connection and try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout title="Sign in" subtitle="VirtueTasks is invite-only.">
      <form onSubmit={onSubmit} className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={needsSecondFactor}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-[12px] text-[var(--accent)] hover:underline">
              Forgot?
            </Link>
          </div>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={needsSecondFactor}
          />
        </div>

        {needsSecondFactor && !useRecovery && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="totp">Authenticator code</Label>
            <Input
              id="totp"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              maxLength={6}
              required
              autoFocus
              className="vt-mono tracking-[0.3em]"
              value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, ''))}
            />
            <button
              type="button"
              className="self-start text-[12px] text-[var(--accent)] hover:underline"
              onClick={() => setUseRecovery(true)}
            >
              Use a recovery code instead
            </button>
          </div>
        )}

        {needsSecondFactor && useRecovery && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="recovery">Recovery code</Label>
            <Input
              id="recovery"
              placeholder="ABCDE-12345"
              required
              autoFocus
              className="vt-mono uppercase"
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
            />
            <button
              type="button"
              className="self-start text-[12px] text-[var(--accent)] hover:underline"
              onClick={() => setUseRecovery(false)}
            >
              Use my authenticator app
            </button>
          </div>
        )}

        {error && (
          <p role="alert" className="rounded-[6px] bg-[var(--danger)]/10 px-2.5 py-2 text-[12px] text-[var(--danger)]">
            {error}
          </p>
        )}

        <Button type="submit" variant="primary" size="lg" disabled={submitting} className="mt-1">
          {submitting ? <Loader2 className="animate-spin" /> : needsSecondFactor ? <ShieldCheck /> : <KeyRound />}
          {needsSecondFactor ? 'Verify and sign in' : 'Sign in'}
        </Button>
      </form>
    </AuthLayout>
  );
}
